import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, ShieldCheck } from "lucide-react";
import PinResetSelfServeDialog from "@/components/PinResetSelfServeDialog";

/**
 * PinGate — Fetches /me/pin-status on mount. If the member has no PIN, force
 * a modal to set a new 4-digit PIN before they can generate any shop
 * redemption code.
 *
 * Exposes render-prop `children({ status, openReset, refresh })` so parents can
 * render a "Forgot PIN?" link that opens the self-serve email-OTP reset flow.
 */
export default function PinGate({ children, onChange }) {
  const [state, setState] = useState({ has_pin: false, has_email: false, loaded: false });
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/me/pin-status");
    setState({ ...data, loaded: true });
    onChange?.(data);
    return data;
  }, [onChange]);
  useEffect(() => { refresh(); }, [refresh]);

  const mustSet = state.loaded && !state.has_pin;

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits");
    if (!/^\d{4}$/.test(confirm)) return toast.error("Please re-enter the same PIN in the second field");
    if (pin !== confirm) return toast.error("The two PINs don't match — please re-enter");
    setBusy(true);
    try {
      await api.post("/me/pin", { pin });
      toast.success("Redemption PIN saved");
      setPin(""); setConfirm("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save PIN");
    } finally { setBusy(false); }
  };

  const openReset = () => {
    if (!state.has_email) {
      toast.error("Please add a verified email to your profile first, or contact support.");
      return;
    }
    setResetOpen(true);
  };

  return (
    <>
      {typeof children === "function" ? children({ status: state, openReset, refresh }) : null}

      {/* Forced dialog when the user has no PIN at all */}
      <Dialog open={mustSet} onOpenChange={() => { /* forced — no dismiss */ }}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-md" data-testid="set-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-pink-400" strokeWidth={1.75} />
              Set your redemption PIN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-400">
              You'll enter this 4-digit PIN each time you redeem points at a tie-up shop. Keep it private — no one from Veloskin will ask for it.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest flex items-center gap-1.5"><KeyRound className="w-3 h-3" />New PIN</Label>
                <Input data-testid="input-new-pin" type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="velo-input mt-2 h-12 text-center font-mono tracking-[0.5em] text-lg" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest">Confirm</Label>
                <Input data-testid="input-confirm-pin" type="password" inputMode="numeric" maxLength={4} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} className="velo-input mt-2 h-12 text-center font-mono tracking-[0.5em] text-lg" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="btn-save-pin" type="button" onClick={save} disabled={busy} className="w-full rounded-full bg-pink-500 hover:bg-pink-400 text-black">
              Save PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self-serve reset flow */}
      <PinResetSelfServeDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        onSuccess={refresh}
      />
    </>
  );
}
